/**
 * BBR Boat Ranking System
 * 
 * This module provides utilities for calculating composite performance scores for boat listings.
 * Rankings are based on three main factors:
 * 1. Reviews & Ratings (highest weight)
 * 2. Booking Performance (medium weight)
 * 3. Pro Staff/Partner Impact (lowest weight)
 */

// Weights for each component in the composite score
const WEIGHTS = {
  REVIEWS: 0.60,  // 60% weight for reviews and ratings
  BOOKINGS: 0.30, // 30% weight for booking performance
  PARTNER: 0.10   // 10% weight for partner impact
};

// Time decay constants for booking recency
const TIME_DECAY = {
  // Half-life in days
  HALF_LIFE: 90, // Booking from 90 days ago has half the value of today's booking
};

/**
 * Calculate a review score based on average rating and number of reviews
 * 
 * @param {Array} reviews - Array of review objects for the listing
 * @returns {number} - Score between 0-1
 */
export function calculateReviewScore(reviews) {
  if (!reviews || reviews.length === 0) {
    return 0;
  }

  // Calculate average rating across all review categories
  let totalRating = 0;
  let ratingCount = 0;

  reviews.forEach(review => {
    // Sum up all the rating categories
    const categoryRatings = [
      review["Listing Accuracy"],
      review["Itinerary & Experience"],
      review["Vessel & Equipment"],
      review["Departure & Return"],
      review["Value"],
      review["Communication"]
    ];

    // Filter out undefined values and calculate the average for this review
    const validRatings = categoryRatings.filter(rating => rating !== undefined && rating !== null);
    if (validRatings.length > 0) {
      const reviewAverage = validRatings.reduce((sum, rating) => sum + rating, 0) / validRatings.length;
      totalRating += reviewAverage;
      ratingCount++;
    }
  });

  const averageRating = ratingCount > 0 ? totalRating / ratingCount : 0;
  
  // Scale average rating to 0-1 (from 0-5 stars)
  const ratingScore = averageRating / 5;
  
  // Calculate quantity score (logarithmically scaled to prevent large listings from dominating)
  // Max out at 25 reviews for full points
  const reviewCountScore = Math.min(Math.log(reviews.length + 1) / Math.log(26), 1);
  
  // Combine quality and quantity scores (70% rating, 30% quantity)
  return (ratingScore * 0.7) + (reviewCountScore * 0.3);
}

/**
 * Calculate a booking performance score based on completed bookings
 * 
 * @param {Array} bookings - Array of booking objects for this listing
 * @param {string} listingId - ID of the listing
 * @returns {number} - Score between 0-1
 */
export function calculateBookingScore(bookings, listingId) {
  if (!bookings || bookings.length === 0) {
    return 0;
  }

  const now = new Date();
  const listingBookings = bookings.filter(booking => booking.listingId === listingId && booking.status === "completed");
  
  if (listingBookings.length === 0) {
    return 0;
  }
  
  // Calculate time-weighted booking score
  let weightedBookingScore = 0;
  
  listingBookings.forEach(booking => {
    const bookingEndDate = new Date(booking.end || booking.endDate || booking.date);
    
    // Skip future bookings or bookings with invalid dates
    if (isNaN(bookingEndDate) || bookingEndDate > now) {
      return;
    }
    
    // Calculate days since booking completion
    const daysSinceCompletion = Math.max(0, (now - bookingEndDate) / (1000 * 60 * 60 * 24));
    
    // Apply time decay (exponential decay with defined half-life)
    const timeDecay = Math.pow(0.5, daysSinceCompletion / TIME_DECAY.HALF_LIFE);
    
    weightedBookingScore += timeDecay;
  });
  
  // Apply logarithmic scaling to prevent large volume operators from completely dominating
  // Max out at about 25 recent bookings for full score
  return Math.min(Math.log(weightedBookingScore + 1) / Math.log(26), 1);
}

/**
 * Calculate a partner impact score based on Pro Staff status and referral activity
 * 
 * @param {Object} ownerDetails - Details about the boat owner
 * @param {string} listingId - ID of the listing
 * @param {Object} referralData - Data about referrals and their impact
 * @returns {number} - Score between 0-1
 */
export function calculatePartnerScore(ownerDetails, listingId, referralData = {}) {
  if (!ownerDetails) {
    return 0;
  }
  
  let score = 0;
  
  // Check if owner is Pro Staff (immediate boost)
  if (ownerDetails.role === "Pro Staff") {
    score += 0.5; // Base score for Pro Staff
  }
  
  // Check referral code usage if available
  if (referralData && referralData.referralCode) {
    const referralCode = referralData.referralCode;
    const referredListings = referralData.referredListings || [];
    const referredBookings = referralData.referredBookings || [];
    
    // Add points for number of referred listings (capped)
    const listingsScore = Math.min(referredListings.length / 10, 0.3);
    
    // Add points for number of bookings from referred listings (capped)
    const bookingsScore = Math.min(referredBookings.length / 20, 0.2);
    
    score += listingsScore + bookingsScore;
  }
  
  // Cap the partner score at 0.9 to ensure it's not dominant
  return Math.min(score, 0.9);
}

/**
 * Calculate the final composite performance score
 * 
 * @param {number} reviewScore - Score from reviews and ratings (0-1)
 * @param {number} bookingScore - Score from booking performance (0-1)
 * @param {number} partnerScore - Score from partner/Pro Staff impact (0-1)
 * @returns {number} - Composite score between 0-1
 */
export function calculateCompositeScore(reviewScore, bookingScore, partnerScore) {
  return (reviewScore * WEIGHTS.REVIEWS) + 
         (bookingScore * WEIGHTS.BOOKINGS) + 
         (partnerScore * WEIGHTS.PARTNER);
}

/**
 * Main function to calculate a boat's performance score
 * 
 * @param {Object} listing - The boat listing object
 * @param {Array} reviews - Reviews for this listing
 * @param {Array} bookings - All bookings in the system
 * @param {Object} ownerDetails - Details about the boat owner
 * @param {Object} referralData - Data about referrals and their impact
 * @returns {number} - Final performance score between 0-1
 */
export function calculateBoatPerformanceScore(listing, reviews, bookings, ownerDetails, referralData) {
  if (!listing) {
    return 0;
  }
  
  // Calculate individual component scores
  const reviewScore = calculateReviewScore(reviews);
  const bookingScore = calculateBookingScore(bookings, listing.id);
  const partnerScore = calculatePartnerScore(ownerDetails, listing.id, referralData);
  
  // Calculate and return the final composite score
  return calculateCompositeScore(reviewScore, bookingScore, partnerScore);
}
